import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { SystemTaskData, TaskData } from './api';

/**
 * The live task map that `useTaskStream` maintains in `AppLayout`, handed down so other views
 * (the Admin page's Background Jobs section) can read it without opening a second SSE
 * connection. `AppLayout` is the only provider; everything else reads via `useTaskMap`.
 */
const TaskMapContext = createContext<Map<string, TaskData>>(new Map());

export const TaskMapProvider = TaskMapContext.Provider;

export function useTaskMap(): Map<string, TaskData> {
  return useContext(TaskMapContext);
}

/**
 * Fires `onComplete` whenever the number of ACTIVE system tasks among `kinds` drops — one
 * call per drop. Used for the per-card refresh flows (e.g. re-fetching card status once an
 * `import` task finishes) and for the admin overview tiles, which refetch when ANY system
 * task finishes.
 *
 * It counts rather than watching the newest matching task: with several kinds in `kinds`, two
 * jobs can overlap, and if the OLDER one finishes second the newest-task watcher never sees an
 * active→inactive edge and the caller never refetches. A count also ignores the 30 s fade —
 * an already-inactive task leaving the map does not change it, so no spurious refetch.
 *
 * Callers should pass a stable `kinds` array (a module-level constant) and a
 * `useCallback`-wrapped `onComplete`.
 */
export function useSystemTaskCompletion(
  kinds: readonly SystemTaskData['kind'][],
  onComplete: () => void,
): void {
  const tasks = useTaskMap();
  const activeCount = useMemo(
    () => [...tasks.values()]
      .filter((t): t is SystemTaskData => t.task_type === 'system' && kinds.includes(t.kind) && t.is_active)
      .length,
    [tasks, kinds],
  );
  const prevCount = useRef(0);
  useEffect(() => {
    const prev = prevCount.current;
    prevCount.current = activeCount;
    if (prev > 0 && activeCount < prev) onComplete();
  }, [activeCount, onComplete]);
}
